import * as THREE from "three";

// GLOBE_RADIUS in three-globe is 100. We build concentric shells around it.
export const R_CORE = 95.5;
export const R_GRATICULE = 98.2;
export const R_GLOW = 99.3;
export const R_SWEEP = 100.9;

function latLngToVec3(lat: number, lng: number, r: number) {
  const phi = THREE.MathUtils.degToRad(90 - lat);
  const theta = THREE.MathUtils.degToRad(90 - lng);
  const sp = Math.sin(phi);
  return new THREE.Vector3(r * sp * Math.cos(theta), r * Math.cos(phi), r * sp * Math.sin(theta));
}

/**
 * Polished obsidian inner core. Pure black diffuse, high metalness —
 * the star dots above mirror into its surface via a live env-map probe.
 */
export function makeCore(envMap?: THREE.CubeTexture | null): THREE.Mesh {
  const geo = new THREE.SphereGeometry(R_CORE, 128, 128);
  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color("#000000"),
    roughness: 0.18,
    metalness: 1.0,
    envMap: envMap ?? null,
    envMapIntensity: 1.3,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "atlas-core";
  return mesh;
}

/**
 * Lat/lng wireframe with fresnel shader — lines fade to transparent at the
 * center of the sphere (facing camera) and glow bright at the silhouette edges.
 */
export function makeGraticule(): THREE.LineSegments {
  const positions: number[] = [];
  const intensities: number[] = [];
  const minorStep = 6; // denser wireframe
  const majorStep = 30;
  const sub = 2; // degrees per line segment

  const push = (lat1: number, lng1: number, lat2: number, lng2: number, w: number) => {
    const a = latLngToVec3(lat1, lng1, R_GRATICULE);
    const b = latLngToVec3(lat2, lng2, R_GRATICULE);
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
    intensities.push(w, w);
  };

  // meridians
  for (let lng = -180; lng < 180; lng += minorStep) {
    const weight = lng % majorStep === 0 ? 1.0 : 0.35;
    for (let lat = -88; lat < 88; lat += sub) {
      push(lat, lng, lat + sub, lng, weight);
    }
  }
  // parallels
  for (let lat = -84; lat <= 84; lat += minorStep) {
    const weight = lat === 0 ? 1.2 : lat % majorStep === 0 ? 1.0 : 0.35;
    for (let lng = -180; lng < 180; lng += sub) {
      push(lat, lng, lat, lng + sub, weight);
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geom.setAttribute(
    "aIntensity",
    new THREE.Float32BufferAttribute(intensities, 1)
  );

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uColor: { value: new THREE.Color("#ffffff") },
      uTime: { value: 0 },
      uPower: { value: 1.6 },
    },
    vertexShader: /* glsl */ `
      attribute float aIntensity;
      varying vec3 vNormal;
      varying vec3 vViewDir;
      varying float vIntensity;
      void main() {
        vec3 n = normalize(position);
        vNormal = normalize(normalMatrix * n);
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewDir = normalize(-mvPosition.xyz);
        vIntensity = aIntensity;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying vec3 vNormal;
      varying vec3 vViewDir;
      varying float vIntensity;
      uniform vec3 uColor;
      uniform float uTime;
      uniform float uPower;
      void main() {
        float facing = max(dot(normalize(vNormal), normalize(vViewDir)), 0.0);
        // strong rim + gentle baseline across the sphere surface
        float fresnel = pow(1.0 - facing, uPower);
        float base = 0.08;
        float breath = 0.85 + 0.15 * sin(uTime * 0.55);
        float alpha = (fresnel + base) * breath * vIntensity * 0.4;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
  });

  const lines = new THREE.LineSegments(geom, mat);
  lines.name = "atlas-graticule";
  return lines;
}

/**
 * Bentley-style starfield — thousands of tiny points on a large surrounding
 * sphere, each with a per-point twinkle phase. Additive blending so they
 * read as pure light.
 */
export function makeStarfield(count = 4500, radius = 420): THREE.Points {
  const positions = new Float32Array(count * 3);
  const phases = new Float32Array(count);
  const sizes = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    // random point on sphere (uniformly distributed)
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    const r = radius * (0.85 + Math.random() * 0.3);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi);
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    phases[i] = Math.random() * Math.PI * 2;
    sizes[i] = 0.5 + Math.pow(Math.random(), 3) * 2.8;
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geom.setAttribute("aPhase", new THREE.BufferAttribute(phases, 1));
  geom.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color("#f4f0e8") },
    },
    vertexShader: /* glsl */ `
      attribute float aPhase;
      attribute float aSize;
      uniform float uTime;
      varying float vTwinkle;
      void main() {
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mv;
        float tw = 0.25 + 0.75 * pow(0.5 + 0.5 * sin(uTime * 1.6 + aPhase), 2.0);
        vTwinkle = tw;
        gl_PointSize = aSize * tw * (260.0 / -mv.z);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying float vTwinkle;
      uniform vec3 uColor;
      void main() {
        vec2 c = gl_PointCoord - vec2(0.5);
        float d = length(c);
        float core = smoothstep(0.5, 0.0, d);
        float halo = smoothstep(0.5, 0.15, d) * 0.35;
        float alpha = (core + halo) * vTwinkle;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
  });

  const pts = new THREE.Points(geom, mat);
  pts.name = "atlas-starfield";
  pts.frustumCulled = false;
  return pts;
}

/**
 * Very faint backlit halo sphere — sits just above the graticule, breathes
 * slowly, and bleeds warm light between the gray core and the dot layer.
 */
export function makeGlow(): THREE.Mesh {
  const geo = new THREE.SphereGeometry(R_GLOW, 64, 64);
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uColor: { value: new THREE.Color("#e8e8f0") },
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vViewDir;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying vec3 vNormal;
      varying vec3 vViewDir;
      uniform vec3 uColor;
      uniform float uTime;
      void main() {
        float facing = max(dot(normalize(vNormal), normalize(vViewDir)), 0.0);
        float rim = pow(1.0 - facing, 3.5);
        float breath = 0.65 + 0.35 * sin(uTime * 0.35);
        gl_FragColor = vec4(uColor, rim * 0.22 * breath);
      }
    `,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "atlas-glow";
  return mesh;
}

/**
 * Rotating narrow arc — a scanning sweep, like radar passing around the globe.
 * Implemented as a thin Torus segment; we rotate it slowly.
 */
export function makeSweep(): THREE.Mesh {
  const geo = new THREE.TorusGeometry(R_SWEEP, 0.35, 16, 256, Math.PI * 1.35);
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uColor: { value: new THREE.Color("#ffffff") },
      uAccent: { value: new THREE.Color("#ff4d1f") },
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying vec2 vUv;
      uniform vec3 uColor;
      uniform vec3 uAccent;
      uniform float uTime;
      void main() {
        // uv.x goes 0..1 along the arc. fade in at head, tail off softly.
        float head = smoothstep(0.88, 1.0, vUv.x);
        float tail = smoothstep(0.0, 0.7, vUv.x);
        float intensity = tail * (1.0 - head * 0.3);
        vec3 col = mix(uAccent, uColor, intensity);
        gl_FragColor = vec4(col, intensity * 0.7);
      }
    `,
  });
  const mesh = new THREE.Mesh(geo, mat);
  // put the arc on the equatorial plane, facing out
  mesh.rotation.x = Math.PI / 2;
  mesh.name = "atlas-sweep";
  return mesh;
}
